import React, {useEffect, useState} from "react";
import QnectTextField from "../qnect-ui/qnect-text-field";
import QnectDateField from "../qnect-ui/qnect-date-field";
import QnectSelectField from "../qnect-ui/qnect-select-field";
import FieldWrapper from "../custom/field-wrapper";
import QnectButton from "../qnect-ui/qnect-button";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "../custom/dialog-title";
import DialogSecondaryTitle from "../custom/dialog-secondary-title";
import Dialog from '../custom/dialog';
import {useDialogStyles} from "../custom/dialog-style";
import useConfig from "../qnect-ui/qnect-config";
import moment from "moment";
import {_generateUuid, _masked, _validateForm, _validateNric} from "../../utils/utility";
import {DialogContentText} from "@material-ui/core";

export const EditUserDialog = ({open = false, onClose, onOk, heading, parentOptions, data, props}) => {
    const classes = useDialogStyles();
    const cfg = useConfig();
    const formId = 'addUserForm';

    const [name, setName] = useState(null);
    const [employmentDate, setEmploymentDate] = useState(null);
    const [effectiveDate, setEffectiveDate] = useState(null);
    const [dateOfBirth, setDateOfBirth] = useState(null);
    const [securityId, setSecurityId] = useState(null);
    const [relationship, setRelationship] = useState(null);
    const [parentMember, setParentMember] = useState(null);
    const [nricErrorMessage, setNricErrorMessage] = useState('');
    const [effectiveDateErrorMessage, setEffectiveDateErrorMessage] = useState('');
    const [employmentDateErrorMessage, setEmploymentDateErrorMessage] = useState('');
    const [relationshipErrorMessage, setRelationshipErrorMessage] = useState('');
    const [subsidiaryNo, setSubsidiaryNo] = useState(data?.subsidiaryNo);
    const [planId, setPlanId] = useState(data?.planId);
    const [emailAddress, setEmailAddress] = useState(props.policy?.emailAddress);
    const [gender, setGender] = useState(props.policy?.gender);
    const [allowModify, setAllowModify] = useState(true);

    useEffect(() => {
        setName(data?.name);
        setEmploymentDate(data?.employmentDate ? moment(data?.employmentDate, 'DD/MM/YYYY') : null);
        setEffectiveDate(data?.effectiveDate ? moment(data?.effectiveDate, 'DD/MM/YYYY') : null);
        setDateOfBirth(data?.dateOfBirth ? moment(data?.dateOfBirth, 'DD/MM/YYYY') : null);
        setSecurityId(data?.securityId);
        setRelationship(data?.relationship);
        setParentMember(data?.parentMember);
        setSubsidiaryNo(data?.subsidiaryNo);
        setPlanId(data?.planId);
        setGender(data?.gender);
        setEmailAddress(data?.emailAddress);
        setNricErrorMessage('');
        setRelationshipErrorMessage('');
        setEffectiveDateErrorMessage('');
        setEmploymentDateErrorMessage('');
        setAllowModify(data?.allowModify);
    },[data, open]);

    const onClickOk = () => {
        if (_validateForm(formId)) {
            setNricErrorMessage('')

            if (!(securityId.startsWith('•') || securityId.startsWith('*')) && !_validateNric(securityId)) {
                setNricErrorMessage(cfg('errorMessage.invalidNric'));
                return;
            }

            if (!validateDates()){
                return;
            }

            // if member is not parent, cannot change plan/subsidiary
            if (relationship !== '00') {
                if (data?.subsidiaryNo !== subsidiaryNo || data?.planId !== planId) {
                    setRelationshipErrorMessage('errorMessage.cannotChangePlan');
                    return;
                }
            }

            let obj = {
                name: name,
                employmentDate: employmentDate,
                effectiveDate: effectiveDate,
                dateOfBirth: dateOfBirth,
                securityId: securityId,
                relationship: relationship,
                subsidiaryNo: subsidiaryNo,
                planId: planId,
                gender: gender,
                emailAddress: emailAddress,
                action: 'E',
                memberNo: data?.memberNo,
                recordIdx: data.tableData.id,
            }

            obj.tempId = data.tempId ? data.tempId : _generateUuid();
            onOk(obj);
        }
    }

    const validateDates = () => {
        let isValid = true;

        /** don't validate with employment date if relationship is not self (00) */
        if (relationship === '00') {
            if (effectiveDate.startOf('day').isBefore(employmentDate.startOf('day'))){
                setEffectiveDateErrorMessage(cfg('effectiveDateUser.errorMessages.effectiveDateLessThanEmploymentDate'));
                isValid = false;
            }

            if (employmentDate.startOf('day').isBefore(dateOfBirth.startOf('day')) ||
                employmentDate.startOf('day').isSame(dateOfBirth.startOf('day'))){
                setEmploymentDateErrorMessage(cfg('employmentDateUser.errorMessages.employmentDateLessThanDOB'));
                isValid = false;
            }
        }

        if (isValid && effectiveDate.diff(employmentDate, 'days') > 90) {
            setEffectiveDateErrorMessage(cfg('effectiveDateUser.errorMessages.effectiveDateMoreThan90'));
            isValid = false;
        }

        return isValid;
    }

    const changeRelationship = (relationship) => {
        if (relationship === '00') {
            setParentMember('');
        }
        setRelationship(relationship);
    }

    return (
        <div>
            <Dialog open={open} onClose={onClose} className={classes.modal_box_container}>
                <DialogTitle className={classes.title}
                    title={cfg("policyDetailModals.editUser.title")}
                    onClose={onClose} lang={props.lang}
                />
                <DialogSecondaryTitle title={heading} />
                <DialogContent>
                     <div className={classes.modal_box_form_container}>
                         <div className='modal_box_form_field input_subsidiary'>
                             <FieldWrapper >
                                 <QnectSelectField
                                     formId={formId}
                                     field="subsidiary"
                                     value={subsidiaryNo}
                                     options={props.subsidiaryList}
                                     disabled={relationship !== '00' || !allowModify}
                                     onChange={(val) => setSubsidiaryNo(val)}
                                 />
                             </FieldWrapper>
                         </div>
                         <div className='modal_box_form_field input_plan'>
                             <FieldWrapper >
                                 <QnectSelectField
                                     formId={formId}
                                     field="plan"
                                     value={planId}
                                     options={props.planList}
                                     disabled={relationship !== '00' || !allowModify}
                                     onChange={(val) => setPlanId(val)}
                                 />
                             </FieldWrapper>
                         </div>
                         <div className='modal_box_form_field input_relationship'>
                             <FieldWrapper >
                                 <QnectSelectField
                                     formId={formId}
                                     field="relationshipUser"
                                     value={relationship}
                                     disabled={true}
                                     onChange={(val) => changeRelationship(val)}
                                     errorMessage={relationshipErrorMessage}
                                 />
                             </FieldWrapper>
                         </div>
                         {
                             relationship && relationship !== '00' &&
                             <div className='modal_box_form_field input_relationship'>
                                 <FieldWrapper >
                                     <QnectSelectField
                                         formId={formId}
                                         field="parentMembers"
                                         value={parentMember}
                                         options={parentOptions}
                                         onChange={ val => setParentMember(val)}
                                         disabled={data?.relationship !== '00' || !allowModify}
                                     />
                                 </FieldWrapper>
                             </div>
                         }
                         { relationship === '00' &&
                             <div className='modal_box_form_field input_employmentDate'>
                                 <FieldWrapper >
                                     <QnectDateField
                                         formId={formId}
                                         field="employmentDateUser"
                                         value={employmentDate}
                                         onChange={ val => {
                                             setEmploymentDate(val)
                                         }}
                                         errorMessage={employmentDateErrorMessage}
                                         disabled={true}
                                     />
                                 </FieldWrapper>
                             </div>
                         }
                         <div className='modal_box_form_field input_name'>
                             <FieldWrapper >
                                 <QnectTextField
                                     formId={formId}
                                     field="nameUser"
                                     value={name}
                                     onChange={ val => {
                                         setName(val.target.value)
                                     }}
                                     label={ relationship !== '00' ? cfg('genericLabels.dependentNameLabel') : null}
                                     disabled={!allowModify}
                                 />
                             </FieldWrapper>
                         </div>
                         <div className='modal_box_form_field input_effectiveDate'>
                             <FieldWrapper >
                                 <QnectDateField
                                     formId={formId}
                                     field="effectiveDateUser"
                                     value={effectiveDate}
                                     onChange={ val => {
                                         setEffectiveDate(val)
                                     }}
                                     errorMessage={effectiveDateErrorMessage}
                                     disabled={data?.relationship !== '00' || !allowModify}
                                 />
                             </FieldWrapper>
                       </div>
                         <div className='modal_box_form_field input_dobDate'>
                             <FieldWrapper >
                                 <QnectDateField
                                     formId={formId}
                                     field="dateOfBirthUser"
                                     value={dateOfBirth}
                                     onChange={ val => {
                                         setDateOfBirth(val)
                                     }}
                                     disabled={!allowModify}
                                 />
                             </FieldWrapper>
                         </div>
                         <div className='modal_box_form_field input_nricPassport'>
                             <FieldWrapper >
                                 <QnectTextField
                                     formId={formId}
                                     field="nricPassportUser"
                                     value={_masked(securityId)}
                                     onBlur={(val) => {
                                         setSecurityId(val.target.value)
                                     }}
                                     errorMessage={nricErrorMessage}
                                     disabled={!allowModify}
                                 />
                             </FieldWrapper>
                         </div>
                         <div className='modal_box_form_field input_gender'>
                             <FieldWrapper >
                                 <QnectSelectField
                                     formId={formId}
                                     field="genderUser"
                                     value={gender}
                                     onChange={(val) => setGender(val)}
                                     disabled={!allowModify}
                                 />
                             </FieldWrapper>
                         </div>
                         <div className='modal_box_form_field input_email'>
                             <FieldWrapper >
                                 <QnectTextField
                                     formId={formId}
                                     field="emailAddress"
                                     value={emailAddress}
                                     onChange={(val) => setEmailAddress(val.target.value)}
                                     disabled={!allowModify}
                                 />
                             </FieldWrapper>
                         </div>
                     </div>
                    { !allowModify && <DialogContentText className="NonModifyMessage">
                        We regret to inform you that we are unable to proceed as changes for the next Renewal period, it must be submitted through Renewal instructions. <br/><br/>For
                        further assistance, you may contact us at <a href="mailto:acchealthupdate.sg@qbe.com">acchealthupdate.sg@qbe.com</a>.
                    </DialogContentText>
                    }
                </DialogContent>
                <DialogActions className={classes.actions}>
                        <QnectButton onClick={onClose} field="cancel" />
                        <QnectButton onClick={() => onClickOk()} field="confirm" disabled={!allowModify}/>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default EditUserDialog;
